import type { Header, Table } from "@tanstack/react-table";
import { cx } from "class-variance-authority";
import type { PropsWithChildren } from "react";

import { ArrowDropDownIcon } from "@/assets/icons/general/ArrowDropDown";
import { usePanelContext } from "@/components/shared/layout/PanelContext";
import { Button } from "@/components/ui/buttons/Button/Button";
import { Dropdown } from "@/components/ui/inputs/Dropdown/Dropdown";
import { Typography } from "@/components/ui/text/Typography/Typography";

interface IProps extends PropsWithChildren {
  header: Header<any, unknown>;
  label?: string;
  align?: "start" | "center" | "end";
  table: Table<any>;
}

const WatchlistTableHeaderCell = ({ header, label, align = "start", table }: IProps) => {
  const allColumns = table?.getAllColumns();
  const visibleColumns = allColumns?.filter((column) => column.getIsVisible());
  const hiddenColumns = allColumns?.filter((column) => !column.getIsVisible());

  // Get the current column order from the table
  const currentColumnOrder = table?.getState().columnOrder || [];

  // Sort visible columns according to the current column order
  const sortedVisibleColumns = visibleColumns?.sort((a, b) => {
    const aIndex = currentColumnOrder.indexOf(a.id);
    const bIndex = currentColumnOrder.indexOf(b.id);

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    return 0;
  });

  const isLastVisibleColumn = sortedVisibleColumns?.[sortedVisibleColumns.length - 1]?.id === header?.column.id;
  const { updatePanelSizes, startAutoResizing } = usePanelContext();
  return (
    <div
      {...{
        className: cx(
          "flex items-center gap-1 px-3 py-1-5 min-w-11",
          align === "start" && "justify-start",
          align === "center" && "justify-center",
          align === "end" && "justify-end",
        ),
      }}
    >
      {isLastVisibleColumn && !!hiddenColumns?.length ? (
        <Dropdown
          label=""
          placeholder=""
          hideLabels
          items={
            hiddenColumns?.map((column) => ({
              label: (column.columnDef.meta as any)?.label || "",
              value: column.id,
              id: column.id,
            })) || []
          }
          value={header?.column.id}
          onChange={(value) => {
            const newColumnSize = table?.getColumn(value)?.getSize() || 0;
            const headerColumnSize = header?.column.getSize() || 0;
            const allVisibleColumnsSize = visibleColumns?.reduce((acc, column) => acc + column.getSize(), 0) || 0;

            const newAllVisibleColumnsSize = allVisibleColumnsSize - headerColumnSize + newColumnSize + 50;

            const newRelativeSize = (100 * newAllVisibleColumnsSize) / window.outerWidth;
            let newColumnOrder = [...(table.getState().columnOrder || [])];
            if (currentColumnOrder.length === 0) {
              newColumnOrder = [...(allColumns?.map((column) => column.id) || [])];
            }
            const headerid = header?.column.id;
            const currentHeaderIndex = newColumnOrder.indexOf(headerid || "");
            const selectedColumnIndex = newColumnOrder.indexOf(value);

            if (currentHeaderIndex !== -1 && selectedColumnIndex !== -1) {
              const selectedColumnId = newColumnOrder[selectedColumnIndex];
              const currentHeaderId = newColumnOrder[currentHeaderIndex];

              newColumnOrder[selectedColumnIndex] = currentHeaderId;
              newColumnOrder[currentHeaderIndex] = selectedColumnId;
            }
            setTimeout(() => {
              table?.setColumnOrder([...newColumnOrder]);
            }, 100);

            table?.getColumn(value)?.toggleVisibility();
            table?.getColumn(header.column.id)?.toggleVisibility();
            startAutoResizing();
            updatePanelSizes(newRelativeSize, 100 - newRelativeSize);
          }}
          customTrigger={
            <Button
              className="!mr-4 !bg-transparent !p-0 relative w-full"
              variant="primary"
              size="small"
              icon={ArrowDropDownIcon}
              iconClassName="absolute -right-4 -top-0-5 w-6 h-6 !text-text-default-primary"
            >
              <Typography
                size="body-4"
                variant={header?.column.getIsSorted() ? "prominent-2" : "default"}
                className="relative text-text-default-primary"
                as="span"
              >
                {label}
              </Typography>
            </Button>
          }
        />
      ) : (
        <Typography
          size="body-4"
          variant={header?.column.getIsSorted() ? "prominent-2" : "default"}
          className="relative text-text-default-primary"
          as="span"
        >
          {label}
        </Typography>
      )}
    </div>
  );
};

export default WatchlistTableHeaderCell;
